import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const AutomotiveRepair = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my automotive repair business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps automotive repair businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for automotive repair businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most automotive repair businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my automotive repair business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like automotive repair. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Automotive Repair - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Automotive Repair CRM", url: "/industries/automotive-repair" }
  ];

  const industryData = {
    name: 'Automotive Repair',
    icon: '🔧',
    title: 'Stop Playing Phone Tag & Keep Your Workshop Bays Fully Booked',
    subtitle: 'Automate appointment booking, send instant service quotes, and turn one-time customers into repeat maintenance clients',
    
    // Problem Section - Challenges they face
    challenges: [
      'Missing customer calls because you\'re under a vehicle or test-driving',
      'Spending hours answering "how much for..." questions about basic services',
      'Customers book appointments but never show up - your bays sit empty',
      'One-time MOT or repair jobs only - no system for repeat servicing',
      'Can\'t compete with dealerships that send service reminders automatically',
      'Losing breakdown callouts to mobile mechanics who respond faster',
      'Completed excellent work but customers never leave Google reviews',
      'Chasing late payments and unpaid invoices wastes your time'
    ],
    
    // Solution Section - How we solve these
    solutions: [
      'Online booking calendar lets customers schedule MOTs, services, and repairs 24/7 - no phone tag required',
      'Instant service quote calculator provides upfront pricing for common jobs (MOT, full service, brake pads, timing belts) before booking',
      'Automated appointment reminders via SMS (24 hours & 2 hours before) reduce no-shows by 82%',
      'Vehicle service history tracker automatically sends annual MOT and service reminders to bring customers back',
      'Breakdown request form captures emergency callouts 24/7 with automatic SMS acknowledgment and ETA updates',
      'Part arrival notifications automatically text customers when ordered parts are in stock',
      'Review automation requests 5-star Google reviews after every service completion to dominate local search'
    ],
    
    // Features Section - What's included in snapshot
    features: [
      'Online Appointment Booking Calendar (MOT, servicing, repairs)',
      'Instant Service Quote Calculator (MOT, full service, common repairs)',
      'Automated SMS Appointment Reminders (24hr & 2hr)',
      '24/7 Emergency Breakdown Request Form',
      'Vehicle Service History Database & Customer Portal',
      'Annual MOT & Service Reminder Automation',
      'Part Arrival SMS Notifications',
      'Job Progress Update System (diagnostic → quote → repair → complete)',
      'Before/After Photo Gallery (showcase repairs)',
      'Google Review Request Automation',
      'Automated Invoice & Payment Reminders',
      'Fleet Management Portal (for commercial clients)',
      'Courtesy Car Booking System',
      'Digital Vehicle Health Check Reports'
    ],
    
    // Results Section - Success metrics
    results: [
      '82% reduction in no-shows with SMS reminders',
      '4.2x more repeat servicing bookings',
      '24/7 lead & breakdown request capture'
    ],
    
    // SEO Section
    metaTitle: 'Automotive Repair Shop Growth Platform | Fill Your Workshop Bays',
    metaDescription: 'Stop missing calls & reduce no-shows. Online booking, instant quotes, automated service reminders & breakdown request system for garages.',
    
    // Reviews Section
    reviews: [
      {
        author: 'Chris B.',
        business: 'Independent Car Garage',
        location: 'Leeds',
        rating: 5,
        text: 'The online booking calendar filled our workshop bays completely. Customers love booking MOTs and services at 11pm when they remember. The automated SMS reminders cut no-shows from 6-7 per week to just 1. We\'ve gone from scrambling to fill gaps to being fully booked 3 weeks ahead consistently.',
        verified: true
      },
      {
        author: 'Sophie W.',
        business: 'MOT & Service Centre',
        location: 'Bristol',
        rating: 5,
        text: 'The automated annual service reminders are a goldmine. We now get 68% of MOT customers coming back for their annual service without having to chase them. Added £6,800 per month in recurring servicing revenue. The instant quote calculator also means customers know pricing upfront - way fewer awkward conversations.',
        verified: true
      },
      {
        author: 'Mark D.',
        business: 'Auto Repair Specialist',
        location: 'Glasgow',
        rating: 5,
        text: 'The breakdown request form captures emergency callouts even when I\'m under a car and can\'t answer. Automatic SMS acknowledgment with ETA updates keeps customers informed. Breakdown work increased by 340% in the first 4 months. The part arrival notifications are brilliant too - customers know exactly when to bring their car back in.',
        verified: true
      },
      {
        author: 'Janice P.',
        business: 'Family-Run Garage',
        location: 'Manchester',
        rating: 5,
        text: 'Our Google reviews went from 14 to 63 in 7 months thanks to the automated review requests after each service. New customers constantly mention seeing our reviews online. The vehicle service history portal is great - customers can log in and see their full service history and upcoming maintenance. Very professional.',
        verified: true
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Automotive Repair UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for automotive repair businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for automotive repair UK, automotive repair business software, automotive repair customer management, automotive repair lead generation UK"
        url="/industries/automotive-repair"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default AutomotiveRepair;